import React, { useMemo } from 'react'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { pluralizeToString } from 'lib/string/pluralize'
import TextLink from 'LuxKit/TextLink'
import { CalendarPickerMonthView } from 'selectors/offerPage/offerPageSelectors'
import MobileMultipleRoomsDurationLabel from 'components/OfferPage/MultipleRoomsBooking/Mobile/MobileMultipleRoomsDurationLabel'

interface Props {
  duration: number
  flightsOnly?: boolean
  enableCalendarRecommendations: boolean
  month: CalendarPickerMonthView
  selected?: boolean
  hasPromo: boolean
  displayMemberPrice?: boolean
  isMultiRoom?: boolean
}

enum DisplayVariant {
  ShowContentPackageAvailable,
  ShowFlightsNotYetAvailable,
  ShowRecommendations,
  ShowFlightOnlyNoCache,
}

function OfferCalendarFlightsMonthTile(props: Props) {
  const {
    month,
    duration,
    flightsOnly,
    enableCalendarRecommendations,
    selected,
    hasPromo,
    displayMemberPrice,
    isMultiRoom,
  } = props

  const flightPriceAvailable = month.hotelTotal < month.packagedTotal

  const displayVariant = useMemo<DisplayVariant>(() => {
    if (month.hotelAvailable) {
      if (month.packagedAvailable) {
        if (flightsOnly && !month.flightCacheActive) {
          return DisplayVariant.ShowFlightOnlyNoCache
        }
        return DisplayVariant.ShowContentPackageAvailable
      } else if (!flightsOnly) {
        return DisplayVariant.ShowFlightsNotYetAvailable
      }
    }
    return DisplayVariant.ShowRecommendations
  }, [month.hotelAvailable, month.packagedAvailable, month.flightCacheActive, flightsOnly])

  const colour = useMemo(() => {
    if (hasPromo) {
      return 'highlight-secondary'
    } else if (displayMemberPrice && !selected) {
      return 'lux-plus'
    } else {
      return undefined
    }
  }, [hasPromo, displayMemberPrice, selected])

  const subTextColour = selected ? 'neutral-one' : 'neutral-three'

  return (<div>
    {displayVariant === DisplayVariant.ShowFlightOnlyNoCache && (
      <Caption variant="medium" colour={subTextColour}>
        {pluralizeToString('night', duration)}
      </Caption>
    )}
    {displayVariant === DisplayVariant.ShowRecommendations && enableCalendarRecommendations && (
      <>
        <Caption variant="large" colour={subTextColour}>Sold out</Caption>
        <TextLink size="small" weight="regular">View similar offers</TextLink>
      </>
    )}
    {
      displayVariant === DisplayVariant.ShowContentPackageAvailable && (<>
        {!isMultiRoom && <Caption variant="large" colour={subTextColour}>
          {pluralizeToString('night', duration)} {flightPriceAvailable && '+ flights '}from
        </Caption>}
        {isMultiRoom && <Caption variant="large" colour={subTextColour}>
          <MobileMultipleRoomsDurationLabel shouldDisplayRoom shouldDisplayFlight />
        </Caption>}
        <div>
          <BodyText colour={colour} as="span" variant="large" weight="bold">
            <FormatCurrency value={month.packagedTotal} />
          </BodyText>{' '}
          <Caption as="span" variant="large" colour={colour ?? subTextColour}>total*</Caption>
          {!flightPriceAvailable &&
            <Caption as="span" variant="large" colour={subTextColour}>
              {' '} + flights
            </Caption>}
        </div>
      </>)
    }
    {
      displayVariant === DisplayVariant.ShowFlightsNotYetAvailable && (<>
        <Caption variant="medium" colour={subTextColour}>Flights not yet available</Caption>
        <TextLink size="small" weight="regular">View hotel only</TextLink>
      </>)
    }
  </div>
  )
}

export default React.memo(OfferCalendarFlightsMonthTile)
